/**
 * Bosnian strings for the submissions screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 */

export const submissions = {
  // ---- Shared by more than one screen ------------------------------
  /*
   * Not a status, despite sitting next to one on the same row.
   *
   * The server sends `isOverdue` as a boolean alongside whatever the status
   * happens to be — a submission is late *and* in progress — so this word is
   * the client's to translate and the badge beside it is not.
   */
  'submissions.overdue': 'Van roka',
  // Not `action.cancel` from common.ts: that one is "Otkaži", and every form
  // on these screens says "Odustani".
  'submissions.cancel': 'Odustani',

  /*
   * The whole status chain in one sentence, for the list and for the detail.
   *
   * The four names are placeholders rather than words, and that is the point:
   * they are enum labels the server owns in four languages, and a copy of
   * "Riješena" here is exactly the drift `submissions.detail.assign.hint`
   * already had to be rewritten to avoid. See `statusChain` in Submissions.tsx
   * for where they are filled in, and why the hint disappears if they cannot be.
   */
  'submissions.status.chain.hint':
    '„{new}” čeka da je neko preuzme, „{inProgress}” da se na njoj radi, „{resolved}” da je posao gotov i čeka potvrdu, a „{closed}” da je prijava završena i da se više ne mijenja — status mijenja nadležni, ne onaj ko je prijavio.',

  // ---- The list ----------------------------------------------------
  'submissions.list.title': 'Prijave',
  'submissions.list.subtitle.all': 'Sve prijave iz pogona koje vidite',
  'submissions.list.subtitle.mine': 'Vaše prijave',
  'submissions.list.filters': 'Filteri',
  'submissions.list.search.placeholder': 'Traži po broju, naslovu ili opisu',
  'submissions.list.search.label': 'Pretraga prijava',

  /*
   * The four selects label themselves for a screen reader only — sighted users
   * read the first option instead, which is why "Svi statusi" and not "Status"
   * is what sits in the closed box.
   */
  'submissions.list.filter.status': 'Status',
  'submissions.list.filter.status.all': 'Svi statusi',
  'submissions.list.filter.type': 'Vrsta',
  'submissions.list.filter.type.all': 'Sve vrste',
  'submissions.list.filter.workCenter': 'Radni centar',
  'submissions.list.filter.workCenter.all': 'Svi radni centri',
  'submissions.list.sort': 'Sortiranje',
  'submissions.list.sort.newest': 'Najnovije prvo',
  'submissions.list.sort.oldest': 'Najstarije prvo',
  'submissions.list.sort.priority': 'Po prioritetu',
  'submissions.list.sort.due': 'Po roku',

  'submissions.list.filter.openOnly': 'Samo otvorene',
  'submissions.list.filter.overdue': 'Samo van roka',
  'submissions.list.filter.unassigned': 'Bez zaduženog',
  'submissions.list.filter.assignedToMe': 'Zadužen sam ja',

  'submissions.list.empty.title': 'Nema prijava',
  'submissions.list.empty.description': 'Promijenite filtere ili napravite novu prijavu.',

  'submissions.list.previous': 'Prethodna',
  'submissions.list.next': 'Sljedeća',

  // ---- One submission ----------------------------------------------
  'submissions.detail.back': 'Nazad na prijave',
  'submissions.detail.print': 'Štampaj',
  'submissions.detail.detailedReport': 'Detaljan izvještaj',
  'submissions.detail.delete': 'Obriši',

  'submissions.detail.field.type': 'Vrsta',
  'submissions.detail.field.due': 'Rok',
  'submissions.detail.field.assignee': 'Zaduženi',
  'submissions.detail.field.resolvedIn': 'Riješeno za',

  /*
   * Two numbers on this screen that people read as something they are not.
   *
   * `due_at` is stamped once, at creation, from the priority chosen then —
   * Submission::booted — so raising the priority afterwards moves nothing, and
   * a supervisor who expects it to is the reason this sentence exists.
   * `resolutionHours` is wall-clock from the report to the moment somebody
   * marked it resolved; nobody subtracts nights or weekends from it.
   */
  'submissions.detail.field.due.hint':
    'Rok se računa sam, prema prioritetu u trenutku prijave — kasnija promjena prioriteta ga ne pomjera.',
  'submissions.detail.field.resolvedIn.hint':
    'Sati od trenutka kada je prijava poslata do trenutka kada je označena kao riješena — računa se stvarno vrijeme, a ne radni sati.',

  'submissions.detail.attachments': 'Prilozi',
  'submissions.detail.attachment.failed': 'Prilog nije moguće otvoriti.',

  'submissions.detail.work': 'Obrada prijave',
  'submissions.detail.assign.label': 'Zaduženi',
  /*
   * The hint names no status on purpose.
   *
   * It used to quote the one a new submission lands in, which meant this
   * sentence held a copy of a label the server owns — the drift this whole
   * layer exists to prevent. The manager sees the real label on the badge a
   * moment later anyway.
   */
  'submissions.detail.assign.hint':
    'Dodjeljivanje nove prijave je istovremeno prebacuje u naredni status.',
  /*
   * Who is allowed to assign, said where the control would be.
   *
   * SubmissionPolicy::assign is RESPONSIBLE, COMMITTEE and ADMIN — and
   * MAINTENANCE is *not* on it, although `isManager()` is true for them
   * everywhere else. The dropdown is now gated on the server's `canAssign`, so
   * a technician no longer meets a 403; what they meet instead is a missing
   * control, and this is the sentence that answers why.
   */
  'submissions.detail.assign.who':
    'Prijavu dodjeljuju nadležni, komisija i administrator; održavanje je rješava, ali je ne može prebaciti na nekog drugog.',
  'submissions.detail.assign.none': 'Niko',
  'submissions.detail.assign.done': 'Prijava je dodijeljena.',
  'submissions.detail.assign.cleared': 'Zaduženje je uklonjeno.',
  'submissions.detail.assign.failed': 'Dodjela nije uspjela.',

  'submissions.detail.status.label': 'Novi status',
  'submissions.detail.status.hint':
    'Ponuđeni su samo statusi u koje prijava smije preći iz trenutnog stanja.',
  // What the change costs, as opposed to which ones are offered. Every
  // transition writes a status event with the actor and the time, and
  // SubmissionWorkflow notifies the reporter unless they moved it themselves.
  'submissions.detail.status.commit':
    'Status mijenja samo nadležni, svaka promjena se upisuje u istoriju sa vašim imenom i vremenom, a onaj ko je prijavio dobije obavještenje.',
  'submissions.detail.status.choose': 'Izaberite',
  'submissions.detail.status.note': 'Napomena',
  'submissions.detail.status.note.placeholder': 'Neobavezno — zašto status mijenjate.',
  'submissions.detail.status.save': 'Sačuvaj status',
  'submissions.detail.status.changed': 'Status je promijenjen.',
  'submissions.detail.status.failed': 'Promjena nije uspjela.',
  // `{status}` is the server's own label for the state the submission is in,
  // passed through at render time rather than spelled out here.
  'submissions.detail.status.locked': 'Prijava je u statusu „{status}” i dalje se ne mijenja.',

  'submissions.detail.history': 'Istorija',
  'submissions.detail.history.created': 'Kreirano',
  // `duration_seconds` on a status event is measured from the event before it,
  // so it belongs to the status the prijava is *leaving* — which is the
  // opposite of how a row that names the new status reads at a glance.
  'submissions.detail.history.duration':
    'Vrijeme uz svaku stavku je koliko je prijava stajala u prethodnom statusu, do te promjene.',

  'submissions.detail.delete.title': 'Obrisati prijavu {reference}?',
  'submissions.detail.delete.irreversible': 'Ovo se ne može poništiti.',
  'submissions.detail.delete.intro': 'Briše se prijava i sve što uz nju ide:',
  /*
   * Three forms per noun, picked by `plural` in lib/utils.ts.
   *
   * The count is interpolated rather than glued in front of the word, because
   * the noun after a number changes shape — 1 prilog, 2 priloga — and in
   * English it changes in a different place. A sentence assembled from
   * fragments is one no translator can repair.
   */
  'submissions.detail.delete.attachments.one': '{count} prilog — fajlovi se brišu i sa servera',
  'submissions.detail.delete.attachments.few': '{count} priloga — fajlovi se brišu i sa servera',
  'submissions.detail.delete.attachments.many': '{count} priloga — fajlovi se brišu i sa servera',
  'submissions.detail.delete.tasks.one': '{count} zadatak',
  'submissions.detail.delete.tasks.few': '{count} zadatka',
  'submissions.detail.delete.tasks.many': '{count} zadataka',
  'submissions.detail.delete.reviews.one': '{count} ocjena',
  'submissions.detail.delete.reviews.few': '{count} ocjene',
  'submissions.detail.delete.reviews.many': '{count} ocjena',
  'submissions.detail.delete.history': 'cijela istorija statusa',
  'submissions.detail.delete.audit':
    'Ko je i kada obrisao ostaje zabilježeno u dnevniku aktivnosti.',
  'submissions.detail.delete.confirm': 'Obriši trajno',
  'submissions.detail.delete.failed': 'Brisanje nije uspjelo.',

  // ---- The improvement block, read and written ---------------------
  // Shared between the detail page and the form, because the same field
  // deserves the same word in both places.
  'submissions.improvement.currentState': 'Trenutno stanje',
  'submissions.improvement.proposal': 'Prijedlog',
  'submissions.improvement.expectedImpact': 'Očekivani efekat',
  'submissions.improvement.duration': 'Trajanje',
  'submissions.improvement.estimatedSavings': 'Procjena uštede',
  'submissions.improvement.estimatedCost': 'Procjena troška',
  'submissions.improvement.actualSavings': 'Ostvarena ušteda',

  // ---- The form ----------------------------------------------------
  'submissions.form.heading': 'Nova prijava',
  'submissions.form.subtitle':
    'Možete poslati i samo glasovnu poruku, fotografiju ili video — opis nije obavezan.',

  /*
   * The two things nobody finds out on their own, said before they start.
   *
   * Both are already in the printed guide under `reporting`, which this links
   * to — same two facts, one sentence each instead of a page.
   */
  'submissions.form.help':
    'Prijavu možete popuniti i kada nema signala — čuva se na telefonu i pošalje se sama čim se veza vrati, pa možete slobodno zatvoriti aplikaciju. Svaka poslata prijava dobije svoj broj (npr. PR-2026-0042) koji možete izgovoriti ili napisati na radni nalog.',

  'submissions.form.type': 'Vrsta prijave',
  /*
   * What the choice *does*, not what the four words mean.
   *
   * A kvar and a bezbjednosni problem take the same road — somebody has to be
   * put on them — while IMPROVEMENT is the one type that changes the form
   * underneath you (SubmissionType::requiresImprovementDetails) and ends in a
   * score and a possible award instead of a repair.
   */
  'submissions.form.type.hint':
    'Kvar, incident i bezbjednosni problem idu nadležnom da ih dodijeli na rješavanje, a prijedlog za poboljšanje otvara dodatna polja i ide na ocjenu koja može donijeti nagradu.',
  'submissions.form.workCenter': 'Radni centar',
  'submissions.form.workCenter.choose': 'Izaberite radni centar',
  // The scanner lands on this same form with `?radniCentar=`, which is the
  // whole reason the stickers exist — and is invisible from here.
  'submissions.form.workCenter.hint':
    'Ako mašina ima QR naljepnicu, skenirajte je umjesto da tražite centar u spisku — radni centar se tada popuni sam.',
  'submissions.form.priority': 'Prioritet',
  // Says "the highest priority" rather than naming it: the priorities and
  // their labels come from /api/meta and this sentence must not hold a copy.
  'submissions.form.priority.hint':
    'Najviši prioritet koristite samo kada proizvodnja stoji ili postoji opasnost.',
  'submissions.form.title': 'Naslov',
  'submissions.form.title.hint': 'Kratko, npr. „Traka proklizava”',
  'submissions.form.title.placeholder': 'npr. Traka proklizava',
  'submissions.form.description': 'Opis',
  'submissions.form.description.placeholder': 'Šta se dešava, od kada, i šta ste do sada probali.',

  'submissions.form.improvement.heading': 'Detalji prijedloga',
  'submissions.form.improvement.currentState.placeholder':
    'Kako se posao radi danas i u čemu je problem.',
  'submissions.form.improvement.proposal.placeholder': 'Šta konkretno promijeniti.',
  'submissions.form.improvement.expectedImpact.placeholder':
    'Ušteda vremena, manje škarta, bezbjednije radno mjesto…',
  'submissions.form.improvement.duration': 'Trajanje sprovođenja',
  'submissions.form.improvement.duration.placeholder': 'npr. 2 sedmice',
  'submissions.form.improvement.possibleDate': 'Mogući datum',
  /*
   * The currency rides in the label here and not on the detail page, where
   * `formatMoney` puts it next to the amount. The input is a bare number, so
   * without it nobody knows whether they are typing marks or euros.
   */
  'submissions.form.improvement.estimatedSavings': 'Procjena uštede ({currency})',
  'submissions.form.improvement.estimatedCost': 'Procjena troška ({currency})',
  /*
   * Said once, on the first of the two amounts, and worded to cover both.
   *
   * The realised figures are a separate record filled in much later and by
   * somebody else — see SubmissionPolicy::recordRealisation, which stays open
   * even after the prijava is closed. Without this, a careful person stalls on
   * a number they cannot possibly know yet.
   */
  'submissions.form.improvement.estimate.hint':
    'Ovo je procjena — stvarni iznos uštede i troška nadležni upisuje kasnije, kad se prijedlog sprovede.',

  'submissions.form.voice.hint':
    'Ispričajte šta ne valja umjesto da kucate. Poruku šaljemo zajedno sa prijavom.',
  // What the paragraph beside it does not say: one recording per prijava, and
  // somebody actually plays it back on the report itself.
  'submissions.form.voice.explain':
    'Ako vam je lakše reći nego otkucati, snimite jednu poruku — nadležni je sluša na samoj prijavi.',
  'submissions.form.media.heading': 'Fotografije i video',
  'submissions.form.media.add': 'Dodajte fotografiju ili video',
  'submissions.form.media.remove': 'Ukloni {name}',
  // The same sentence as the guide's `reporting` step, because it is the one
  // habit that decides whether a prijava can be acted on or has to be chased.
  'submissions.form.media.hint':
    'Fotografija je ono što prijavu čini rješivom — onaj ko dolazi na popravku vidi kvar prije nego krene i zna šta da ponese.',

  'submissions.form.submit': 'Pošalji prijavu',
  'submissions.form.saveToDevice': 'Sačuvaj na uređaj',
  'submissions.form.offlineNotice':
    'Trenutno nema veze sa serverom. Prijava se čuva na uređaju i biće poslata automatski čim se veza vrati — možete slobodno zatvoriti aplikaciju.',

  'submissions.form.needContent': 'Dodajte opis, glasovnu poruku ili prilog.',
  'submissions.form.needContent.toast':
    'Dodajte opis, glasovnu poruku ili prilog prije čuvanja.',
  'submissions.form.savedOffline':
    'Nema veze — prijava je sačuvana na uređaju i poslaće se automatski.',
  'submissions.form.savedAfterFailure':
    'Slanje nije uspjelo, ali je prijava sačuvana i biće poslata automatski.',
  'submissions.form.saveFailed': 'Čuvanje na uređaju nije uspjelo.',
  'submissions.form.sent': 'Prijava {reference} je poslata.',
  'submissions.form.sendFailed': 'Slanje nije uspjelo.',

  // ---- Tasks hanging off a submission ------------------------------
  // `submissions.tasks.*`, not `tasks.*`: this is the section inside a
  // submission, not the standalone tasks screen, and the keys are globally
  // flat so the two would otherwise collide.
  'submissions.tasks.title': 'Zadaci',
  'submissions.tasks.add': 'Novi zadatak',
  /*
   * Why a prijava is broken into tasks at all, and what finishing them does
   * not do. Nothing in TaskController moves the submission when its last task
   * is marked done — the status is the supervisor's to change, and a manager
   * waiting for it to close itself waits forever.
   */
  'submissions.tasks.help':
    'Kad rješavanje ima više koraka, napravite zadatak za svaki — svaki ima svog izvršioca i svoj rok, i svaki se posebno zatvara. Prijava ostaje otvorena dok je vi ne prebacite dalje, bez obzira na to koliko je zadataka završeno.',
  'submissions.tasks.empty.manager':
    'Nema zadataka. Dodijelite zadatak da bi neko preuzeo rješavanje.',
  'submissions.tasks.empty': 'Za ovu prijavu još nema zadataka.',
  'submissions.tasks.due': 'rok {date}',

  /*
   * Verbs, not statuses. "Započni" is what the button asks you to do; what the
   * task then *is* comes back from the server as `statusLabel` and is rendered
   * on the badge beside it.
   */
  'submissions.tasks.start': 'Započni',
  'submissions.tasks.finish': 'Završi',
  'submissions.tasks.verify': 'Verifikuj',

  'submissions.tasks.updated': 'Zadatak je ažuriran.',
  'submissions.tasks.updateFailed': 'Ažuriranje nije uspjelo.',
  'submissions.tasks.assigned': 'Zadatak je dodijeljen.',

  'submissions.tasks.form.title': 'Zadatak',
  'submissions.tasks.form.title.placeholder': 'npr. Zamijeniti ležaj na hoblu',
  'submissions.tasks.form.assignee': 'Zadužen',
  'submissions.tasks.form.assignee.choose': 'Izaberite osobu',
  'submissions.tasks.form.description': 'Opis',
  'submissions.tasks.form.description.placeholder':
    'Neobavezno — šta tačno treba uraditi, koji dijelovi su potrebni.',
  'submissions.tasks.form.due': 'Rok',
  'submissions.tasks.form.due.hint': 'Ne može biti u prošlosti.',
  'submissions.tasks.form.submit': 'Dodijeli zadatak',

  // ---- The reward scheme -------------------------------------------
  /*
   * The reviewer's three buttons, and deliberately NOT the server's
   * `reviewDecisions` labels.
   *
   * They share the enum's values but not its words: the server says what a
   * decision *is* — "Prihvaćeno", "Odbijeno" — and these say what you are about
   * to *do*. An imperative on a button and a past participle on a badge are
   * different strings in every one of these languages, so this is a rare case
   * where a client-side copy is correct rather than drift.
   */
  'submissions.reviews.decide.accept': 'Prihvati',
  'submissions.reviews.decide.pending': 'Na čekanju',
  'submissions.reviews.decide.reject': 'Odbij',
  'submissions.reviews.title': 'Ocjene',
  'submissions.reviews.add': 'Dodaj ocjenu',
  'submissions.reviews.empty.manager':
    'Još nema ocjena. Nadležni daje prvu ocjenu, komisija donosi konačnu odluku i nagradu.',
  'submissions.reviews.empty': 'Prijava još nije ocijenjena.',

  'submissions.reviews.innovation': 'Inovativnost',
  'submissions.reviews.materialBenefit': 'Materijalna korist',
  'submissions.reviews.nonMaterialBenefit': 'Nematerijalna korist',
  'submissions.reviews.total': 'Ukupno',
  // `{amount}` arrives already formatted, currency and all, from formatMoney.
  'submissions.reviews.award': 'Nagrada: {amount}',

  'submissions.reviews.decision': 'Odluka',
  'submissions.reviews.points.hint':
    'Bodovi se dodjeljuju samo prijedlozima za poboljšanje, po tri kriterijuma, svaki 0–5.',
  'submissions.reviews.points.sum': 'Zbir bodova',
  'submissions.reviews.points.override': 'Korigovani ukupan broj',
  'submissions.reviews.points.override.hint': 'Ostavite prazno da se koristi zbir.',
  'submissions.reviews.awardField': 'Nagrada ({currency})',
  'submissions.reviews.awardField.hint': 'Samo komisija može dodijeliti novčanu nagradu.',
  'submissions.reviews.comment': 'Obrazloženje',
  'submissions.reviews.comment.placeholder': 'Zašto je prijedlog prihvaćen ili odbijen.',
  'submissions.reviews.save': 'Sačuvaj ocjenu',
  'submissions.reviews.saved': 'Ocjena je sačuvana.',

  // ---- Spoken messages ---------------------------------------------
  // The label is also what the player announces to a screen reader, which is
  // why the aria strings below interpolate it rather than repeating it.
  'submissions.voice.label': 'Glasovna poruka',
  'submissions.voice.record': 'Snimi glasovnu poruku',
  'submissions.voice.stop': 'Zaustavi',
  'submissions.voice.again': 'Snimi ponovo',
  'submissions.voice.discard': 'Obriši glasovnu poruku',
  'submissions.voice.unavailable':
    'Snimanje zvuka nije dostupno. Aplikaciji je potreban mikrofon i sigurna veza (https).',
  'submissions.voice.empty': 'Snimak je prazan. Provjerite mikrofon i pokušajte ponovo.',
  'submissions.voice.play': 'Pusti {label}',
  'submissions.voice.pause': 'Pauziraj {label}',
  'submissions.voice.download': 'Preuzmi {label}',
  'submissions.voice.position': 'Pozicija u snimku — {label}',
  'submissions.voice.unsupported':
    'Ovaj pregledač ne može da pusti ovaj format — preuzmite snimak.',
  'submissions.voice.loadFailed': 'Poruku nije moguće učitati.',

  // ---- Who was working the centre ----------------------------------
  'submissions.workers.choose': 'Izaberite radnike',
  // Three forms again, and on a button this time — see the delete dialog above.
  'submissions.workers.count.one': '{count} radnik',
  'submissions.workers.count.few': '{count} radnika',
  'submissions.workers.count.many': '{count} radnika',
  'submissions.workers.title': 'Radnici na centru',
  'submissions.workers.clear': 'Poništi izbor',
  'submissions.workers.done': 'Gotovo',
  'submissions.workers.hint':
    'Ko je radio na ovom centru u periodu koji ocjenjujete. Ocjenjuje se radni centar, ne radnik — ovo je podatak o tome ko ga je održavao.',
  'submissions.workers.search.placeholder': 'Ime ili šifra',
  'submissions.workers.search.label': 'Pretraga radnika',
  'submissions.workers.loading': 'Učitavanje radnika…',
  'submissions.workers.unavailable': 'Spisak nije dostupan',
  'submissions.workers.noResults': 'Nema rezultata',
  'submissions.workers.noResults.description': 'Promijenite pretragu.',
  'submissions.workers.narrowed': 'Prikazani su radnici raspoređeni na ovaj radni centar.',
  'submissions.workers.showEveryone': 'Prikaži sve',
  'submissions.attachment.open': 'Otvori prilog {name}',
  'submissions.edit.title': 'Izmjena prijave',
  'submissions.edit.back': 'Nazad na prijavu',
  'submissions.edit.saved': 'Prijava je izmijenjena.',
  'submissions.edit.action': 'Izmijeni',
  'submissions.edit.closed':
    'Ova prijava se više ne može mijenjati — neko ju je već preuzeo ili je zatvorena. Dodajte komentar ili zamolite odgovorno lice.',
  'submissions.edit.scoredLocked':
    'Komisija je već ocijenila ovaj prijedlog, pa se njegov sadržaj više ne mijenja. Ocjena je data upravo na ovaj tekst.',
  'submissions.edit.files.hint':
    'Prilozi se mijenjaju odmah — dugme „Odustani" ispod ne vraća obrisani fajl.',
  'submissions.edit.files.add': 'Dodaj prilog',
  'submissions.edit.files.added': 'Prilog je dodan.',
  'submissions.edit.files.remove': 'Obriši',
  'submissions.edit.files.removed': 'Prilog je obrisan.',
  'submissions.edit.files.removeTitle': 'Obrisati „{name}"?',
  'submissions.edit.files.removeBody': 'Fajl se briše trajno, ne samo sa spiska. Ovo se ne može poništiti.',
  'submissions.realisation.title': 'Realizacija',
  'submissions.realisation.hint':
    'Šta je prijedlog stvarno donio, kada je sproveden i ko ga je sproveo. Popunjava se poslije realizacije — i poslije zatvaranja prijave.',
  'submissions.realisation.by': 'Realizator',
  'submissions.realisation.byNobody': 'Nije evidentiran',
  'submissions.realisation.date': 'Datum realizacije',
  'submissions.realisation.dateHint': 'Ne može biti u budućnosti.',
  'submissions.realisation.actualCost': 'Ostvareni trošak',
  'submissions.realisation.against': 'Procijenjeno: {amount}',
  'submissions.realisation.recorded': 'Realizacija je evidentirana.',
  'submissions.realisation.saved': 'Realizacija je sačuvana.',

  // --- One report, as charts -----------------------------------------
  //
  // The page said what happened and never how long any of it took, while the
  // durations sat unused in the status events.
  'submissions.insights.lifecycle.title': 'Gdje je vrijeme otišlo',
  'submissions.insights.lifecycle.hint': 'Koliko je prijava provela u svakoj fazi.',
  'submissions.insights.lifecycle.series': 'Trajanje',
  'submissions.insights.lifecycle.current': 'Faza u kojoj je prijava sada.',
  'submissions.insights.benchmark.title': 'Brzo ili sporo?',
  'submissions.insights.benchmark.thisOne': 'Ova prijava',
  'submissions.insights.benchmark.usualHere': 'Uobičajeno na ovom radnom centru',
  'submissions.insights.benchmark.usualType': 'Uobičajeno za ovu vrstu prijave',
  'submissions.insights.benchmark.sample': 'Poređeno sa {count} riješenih prijava.',
  'submissions.insights.scoring.title': 'Kako je ocijenjeno',
  'submissions.insights.scoring.hint': 'Iz čega se sastoji ocjena komisije.',
  'submissions.insights.scoring.innovation': 'Inovativnost',
  'submissions.insights.scoring.material': 'Materijalna korist',
  'submissions.insights.scoring.nonMaterial': 'Nematerijalna korist',
  'submissions.insights.scoring.points': 'Bodovi',
  'submissions.insights.scoring.total': 'Ukupno: {total} bodova.',
  'submissions.insights.scoring.median': 'prosjek prihvaćenih prijedloga: {median}',
  'submissions.insights.realisation.title': 'Obećano i ostvareno',
  'submissions.insights.realisation.hint': 'Procjena iz prijedloga naspram onoga što se stvarno desilo.',
  'submissions.insights.realisation.savings': 'Ušteda',
  'submissions.insights.realisation.cost': 'Trošak',
  'submissions.insights.realisation.estimated': 'Procjena',
  'submissions.insights.realisation.actual': 'Ostvareno',
  'submissions.insights.recurrence.title': 'Ponavlja li se ovo?',
  'submissions.insights.recurrence.hint':
    'Isti radni centar ima još {count} ovakvih prijava u posljednjih {days} dana.',
  'submissions.insights.recurrence.series': 'Prijava',

  'submissions.tasks.progress': 'Završeno {done} od {total} zadataka.',

  // ---- The list as queues ------------------------------------------
  //
  // The tab names are the client's: they are views, not statuses, and "U
  // radu" here holds three of the server's statuses at once. See
  // SubmissionFilters::TABS on the server for what each one holds.
  'submissions.tabs.label': 'Pregled prijava',
  'submissions.tab.otvorene': 'Otvorene',
  'submissions.tab.nove': 'Nove',
  'submissions.tab.u-radu': 'U radu',
  'submissions.tab.van-roka': 'Van roka',
  'submissions.tab.rijesene': 'Riješene',
  'submissions.tab.dodijeljene-meni': 'Dodijeljene meni',
  'submissions.tab.moje': 'Moje prijave',
  'submissions.tab.sve': 'Sve',
  // The two tabs whose names do not say exactly what is in them.
  'submissions.tabs.hint':
    '„U radu” su sve otvorene prijave koje je neko preuzeo. „Riješene” čekaju da ih nadležni potvrdi i zatvori.',

  'submissions.filter.type': 'Vrsta',
  'submissions.filter.priority': 'Prioritet',
  'submissions.filter.status': 'Status',
  'submissions.filter.workCenter': 'Mašina',
  'submissions.filter.plant': 'Pogon',
  'submissions.filter.period': 'Period',
  'submissions.filter.unassigned': 'Bez zaduženog',
  'submissions.filter.overdue': 'Van roka',
  // `{label}` is the chip's own text, so a screen reader hears which one goes.
  'submissions.filter.remove': 'Ukloni filter: {label}',
  'submissions.filter.clear': 'Očisti filtere',
  'submissions.period.7d': 'Zadnjih 7 dana',
  'submissions.period.30d': 'Zadnjih 30 dana',
  'submissions.period.90d': 'Zadnjih 90 dana',
  'submissions.period.month': 'Ovaj mjesec',
  'submissions.period.lastMonth': 'Prošli mjesec',
  'submissions.period.custom': '{from} – {to}',

  'submissions.col.reference': 'Broj',
  'submissions.col.title': 'Naslov',
  'submissions.col.machine': 'Mašina · Pogon',
  'submissions.col.type': 'Vrsta',
  'submissions.col.priority': 'Prioritet',
  'submissions.col.status': 'Status',
  'submissions.col.due': 'Rok',
  'submissions.col.assignee': 'Zaduženi',
  'submissions.col.created': 'Prijavljeno',
  'submissions.col.sortBy': 'Sortiraj po koloni {column}',

  'submissions.card.due': 'Rok {date}',
  'submissions.card.nobody': 'Niko nije zadužen',
  'submissions.card.noDue': 'Bez roka',

  /*
   * One sentence per tab for an empty list: what would be here, and — where
   * there is one — how to make it appear. The title is shared.
   */
  'submissions.empty.title': 'Ovdje trenutno nema prijava',
  'submissions.empty.otvorene':
    'Ovdje stoje prijave na kojima se još radi. Novu prijavu pravite dugmetom „+ Novo” gore desno.',
  'submissions.empty.nove': 'Ovdje stižu nove prijave dok ih niko ne preuzme.',
  'submissions.empty.u-radu': 'Ovdje su prijave koje je neko preuzeo, a još nisu riješene.',
  'submissions.empty.van-roka': 'Ovdje se pojavljuju otvorene prijave kojima je prošao rok. Sada nijedna nije kasna.',
  'submissions.empty.rijesene': 'Ovdje čekaju riješene prijave dok ih nadležni ne potvrdi i zatvori.',
  'submissions.empty.dodijeljene-meni': 'Ovdje se pojavljuju otvorene prijave koje su dodijeljene vama.',
  'submissions.empty.moje': 'Ovdje su sve prijave koje ste vi poslali. Novu pravite dugmetom „+ Novo” gore desno.',
  'submissions.empty.sve': 'Ovdje su sve prijave iz vaših pogona, i otvorene i zatvorene.',
  'submissions.empty.filtered.title': 'Ništa ne odgovara filterima',
  'submissions.empty.filtered': 'Uklonite neki od filtera iznad ili pogledajte drugu karticu.',

  // ---- One report: where it stands, and what comes next ------------
  'submissions.stepper.label': 'Tok prijave',
  'submissions.stepper.position': 'Korak {current} od {total}',

  'submissions.next.title': 'Sljedeći korak',
  /*
   * Verbs on buttons — what the press does — and never the status it lands
   * in, which is the server's word and sits on the badge. See
   * lib/submissionWorkflow.ts for which one is offered from where.
   */
  'submissions.next.review': 'Uzmi u pregled',
  'submissions.next.backToReview': 'Vrati u pregled',
  'submissions.next.reconsider': 'Ponovo razmotri',
  'submissions.next.assigned': 'Označi kao dodijeljenu',
  'submissions.next.start': 'Počni rad',
  'submissions.next.reopen': 'Vrati u rad',
  'submissions.next.resolve': 'Označi riješeno',
  'submissions.next.confirmClose': 'Potvrdi i zatvori',
  'submissions.next.close': 'Zatvori',
  'submissions.next.reject': 'Odbij',
  // A fallback for a status the client does not know yet; `{status}` is the
  // server's label.
  'submissions.next.generic': 'Prebaci u „{status}”',
  'submissions.next.dialog.title': '{action} · {reference}',
  'submissions.next.reason': 'Razlog',
  'submissions.next.reason.hint': 'Onaj ko je prijavio vidi ovaj razlog.',
  'submissions.next.reason.placeholder': 'npr. Duplikat prijave PR-2026-0042',
  // Closing asks too, since the customer decided it should: how it ended,
  // which a rejection's example would not describe.
  'submissions.next.reason.close': 'Kako je završeno',
  'submissions.next.reason.close.placeholder': 'npr. Ležaj zamijenjen, mašina radi normalno.',
  'submissions.next.reason.missing': 'Napišite razlog.',

  // ---- The history timeline ----------------------------------------
  //
  // Noun phrases rather than "{actor} je poslao…": a past-tense verb in these
  // languages has a gender, and the server sends a name, not a person. The
  // actor and the time sit on the line under each entry instead.
  'submissions.history.created': 'Prijava je poslata',
  'submissions.history.status': '{from} → {to}',
  'submissions.history.assigned': 'Dodijeljeno: {name}',
  'submissions.history.unassigned': 'Zaduženje je uklonjeno',
  'submissions.history.taskCreated': 'Novi zadatak: „{title}”',
  'submissions.history.taskCreatedFor': 'Novi zadatak za {name}: „{title}”',
  'submissions.history.taskDone': 'Zadatak završen: „{title}”',
  'submissions.history.taskVerified': 'Zadatak provjeren: „{title}”',
  'submissions.history.review': '{type}: {decision}',
  'submissions.history.more': 'Prikaži sve ({count})',

  // The charts move down the page, folded, under this heading.
  'submissions.insights.section': 'Analiza prijave',

  // ---- The new-report form -----------------------------------------
  'submissions.form.machine.heading': 'Na kojoj mašini?',
  'submissions.form.machine.search': 'Traži mašinu',
  'submissions.form.machine.scan': 'Skeniraj QR',
  'submissions.form.machine.change': 'Promijeni',
  'submissions.form.machine.yours': 'Vaše mašine',
  'submissions.form.machine.none': 'Nijedna mašina ne odgovara pretrazi.',
  'submissions.form.machine.required': 'Izaberite mašinu.',
  'submissions.form.machine.scanTitle': 'Skenirajte QR naljepnicu na mašini',
  'submissions.form.machine.scanFailed':
    'Ovaj QR kod ne pripada nijednoj mašini koju vidite. Izaberite je sa spiska.',
  'submissions.form.machine.scanOffline':
    'Za QR je potrebna veza. Izaberite mašinu sa spiska — prijava se i dalje čuva na uređaju.',
  'submissions.form.machine.unknown': 'Mašina iz linka nije pronađena — izaberite je sa spiska.',

} as const

export type SubmissionsSlice = Record<keyof typeof submissions, string>
